export const defaultOptions = {
  selector: '.siema',
  duration: 200,
  easing: 'ease-out',
  perPage: 1,
  startIndex: 0,
  draggable: true,
  multipleDrag: true,
  slidePage: false,
  threshold: 20,
  loop: false,
  rtl: false,
  dotNav: false, // generate dots navigation, it is false by default for backward compatibility
  dotNavClass: 'siema-dot-nav',
  dotClass: 'siema-dot',
  automatedTimer: 10000,
  onInit: () => {},
  onChange: () => {},
};

export default class Siema {
  /**
   * @param {Object} options
   */
  constructor(options) {
    this.config = { ...defaultOptions, ...options };

    // THIS IS BAD, because it is the actual element and not the selector itself, be careful
    // the provided "selector" should be always a string
    this.element = this.selector = typeof this.config.selector === 'string' ? document.querySelector(this.config.selector) : this.config.selector;

    if (this.element === null) {
      throw new Error('Something wrong with your selector');
    }

    // Update perPage attribute depending on the options and viewport
    this.resolveSlidesNumber();

    this.elementWidth = this.element.offsetWidth;
    this.innerElements = Array.from(this.element.children);
    this.currentSlide = this.config.loop ?
      this.config.startIndex % this.innerElements.length :
      Math.max(0, Math.min(this.config.startIndex, this.innerElements.length - this.perPage));
    if (this.config.loop) {
      this.loopSlidesPrepended = [];
      this.loopSlidesAppended = [];
    }
    this.focusableElements = 'a, input, select, textarea, button, iframe, [tabindex], [contentEditable]';
    this.hideA11y = (wrapper) => {
      wrapper.setAttribute('aria-hidden', 'true');
      wrapper.querySelectorAll(this.focusableElements).forEach(focusable => focusable.setAttribute('tabindex', '-1'));
    };
    this.showA11y = (wrapper) => {
      wrapper.removeAttribute('aria-hidden');
      wrapper.querySelectorAll(this.focusableElements).forEach(focusable => focusable.removeAttribute('tabindex'));
    };

    // Bind all event handlers for referencability
    [
      'resizeHandler',
      'touchstartHandler',
      'touchendHandler',
      'touchmoveHandler',
      'mousedownHandler',
      'mouseupHandler',
      'mouseleaveHandler',
      'mousemoveHandler',
      'clickHandler',
      'btnNextHandler',
      'btnPrevHandler',
      'dotClickHandler'
    ].forEach(method => {
      this[method] = this[method].bind(this);
    });

    if (!options.dotNav) {
      this.dots = options.dots ? Array.from(document.querySelectorAll(options.dots)) : null;
    }

    this.btnNext = options.btnNext ? document.querySelector(options.btnNext) : null;
    this.btnPrev = options.btnPrev ? document.querySelector(options.btnPrev) : null;

    // Build markup and apply required styling to elements
    this.init();
  }

  /**
   * Attaches listeners to required events.
   */
  attachEvents() {
    // Resize element on window resize
    window.addEventListener('resize', this.resizeHandler);

    // prev & next button support
    if (this.btnNext) this.btnNext.addEventListener('click', this.btnNextHandler);
    if (this.btnPrev) this.btnPrev.addEventListener('click', this.btnPrevHandler);

    // tab controls (dots)
    if (this.dots) {
      this.dots.forEach((dot, index) => dot.addEventListener('click', this.dotClickHandler(index)));
    }

    // If element is draggable / swipable, add event handlers
    if (this.config.draggable) {
      // Keep track pointer hold and dragging distance
      this.pointerDown = false;
      this.drag = {
        startX: 0,
        endX: 0,
        startY: 0,
        letItGo: null,
        preventClick: false,
      };

      // Touch events
      this.element.addEventListener('touchstart', this.touchstartHandler);
      this.element.addEventListener('touchend', this.touchendHandler);
      this.element.addEventListener('touchmove', this.touchmoveHandler);

      // Mouse events
      this.element.addEventListener('mousedown', this.mousedownHandler);
      this.element.addEventListener('mouseup', this.mouseupHandler);
      this.element.addEventListener('mouseleave', this.mouseleaveHandler);
      this.element.addEventListener('mousemove', this.mousemoveHandler);

      // Click
      this.element.addEventListener('click', this.clickHandler);
    }
  }

  /**
   * Detaches listeners from required events.
   */
  detachEvents() {
    window.removeEventListener('resize', this.resizeHandler);
    if (this.btnNext) this.btnNext.removeEventListener('click', this.btnNextHandler);
    if (this.btnPrev) this.btnPrev.removeEventListener('click', this.btnPrevHandler);
    this.element.removeEventListener('touchstart', this.touchstartHandler);
    this.element.removeEventListener('touchend', this.touchendHandler);
    this.element.removeEventListener('touchmove', this.touchmoveHandler);
    this.element.removeEventListener('mousedown', this.mousedownHandler);
    this.element.removeEventListener('mouseup', this.mouseupHandler);
    this.element.removeEventListener('mouseleave', this.mouseleaveHandler);
    this.element.removeEventListener('mousemove', this.mousemoveHandler);
    this.element.removeEventListener('click', this.clickHandler);
  }

  /**
   * Builds the markup and attaches listeners to required events.
   */
  init() {
    // hide everything out of selector's boundaries
    this.element.style.overflow = 'hidden';

    // rtl or ltr
    this.element.style.direction = this.config.rtl ? 'rtl' : 'ltr';

    // build a frame and slide to a currentSlide
    this.buildSliderFrame();

    // initialize automation
    if (this.config.automated) this.initAutomation();

    this.config.onInit.call(this);
  }

  btnNextHandler() {
    this.next();
    if (this.autoplay) this.autoplay = window.clearInterval(this.autoplay);
  }

  btnPrevHandler() {
    this.prev();
    if (this.autoplay) this.autoplay = window.clearInterval(this.autoplay);
  }

  dotClickHandler(index) {
    return (event) => {
      event.preventDefault();

      if (this.config.automated) {
        this.autoplay = window.clearInterval(this.autoplay);
      }

      if (this.config.slidePage) {
        this.goTo(index * this.perPage);
        return;
      }

      this.goTo(index);
    }
  }

  /**
   * Build a dot navigation dynamically
   */
  buildDotNavigation() {
    this.dotNav = document.createElement('div');

    this.dots = this.innerElements.flatMap((element, index) => {
      // If slidePage option is enabled create a single dot per page
      if (this.config.slidePage && this.perPage > 1 && (index + 1) % this.perPage !== 1) return [];
      
      if (!this.config.slidePage && index > this.innerElements.length - this.perPage) return [];

      const dot = document.createElement('a');
      dot.href = `#slide-${index + 1}`;
      dot.dataset.slide = index;
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.classList.add(this.config.dotClass);
      return [dot];
    });

    this.dotNav.append(...this.dots);

    this.dotNav.classList.add(...[this.config.dotNavClass].flat());
    this.element.appendChild(this.dotNav);
  }

  /**
   * Build a sliderFrame and slide to a current item.
   */
  buildSliderFrame() {
    const widthItem = this.elementWidth / this.perPage;
    const itemsToBuild = this.config.loop ? this.innerElements.length + (2 * this.perPage) : this.innerElements.length;

    // Create frame and apply styling
    this.sliderFrame = document.createElement('div');
    this.sliderFrame.style.width = `${widthItem * itemsToBuild}px`;
    this.enableTransition();

    if (this.config.draggable) {
      this.element.style.cursor = 'grab';
    }

    // Create a document fragment to put slides into it
    const docFragment = document.createDocumentFragment();
    const createLoopSlide = (slide, loopSlides) => {
      const el = this.buildSliderFrameItem(slide.cloneNode(true));
      this.hideA11y(el);
      docFragment.appendChild(el);
      loopSlides.push(el);
    };

    // Loop through the slides, add styling and add them to document fragment
    if (this.config.loop) {
      for (let i = this.innerElements.length - this.perPage; i < this.innerElements.length; i++) {
        createLoopSlide(this.innerElements[i], this.loopSlidesPrepended);
      }
    }
    for (let i = 0; i < this.innerElements.length; i++) {
      const element = this.buildSliderFrameItem(this.innerElements[i]);
      docFragment.appendChild(element);
    }
    if (this.config.loop) {
      for (let i = 0; i < this.perPage; i++) {
        createLoopSlide(this.innerElements[i], this.loopSlidesAppended);
      }
    }

    // Add fragment to the frame
    this.sliderFrame.appendChild(docFragment);

    // Clear selector (just in case something is there) and insert a frame
    this.element.innerHTML = '';
    this.element.appendChild(this.sliderFrame);

    // build dots navigation
    if (this.config.dotNav) {
      this.buildDotNavigation();
    }

    this.detachEvents();
    this.attachEvents();

    // Go to currently active slide after initial build
    this.slideToCurrent();

    this.change();
  }

  buildSliderFrameItem(elm) {
    const elementContainer = document.createElement('div');
    elementContainer.style.cssFloat = this.config.rtl ? 'right' : 'left';
    elementContainer.style.float = this.config.rtl ? 'right' : 'left';
    elementContainer.style.width = `${this.config.loop ? 100 / (this.innerElements.length + (this.perPage * 2)) : 100 / (this.innerElements.length)}%`;
    elementContainer.appendChild(elm);
    return elementContainer;
  }

  /**
   * Determinates slides number accordingly to clients viewport.
   */
  resolveSlidesNumber() {
    if (typeof this.config.perPage === 'number') {
      this.perPage = this.config.perPage;
    }
    else if (typeof this.config.perPage === 'object') {
      this.perPage = 1;
      for (const viewport in this.config.perPage) {
        if (window.innerWidth >= viewport) {
          this.perPage = this.config.perPage[viewport];
        }
      }
    }
  }

  /**
   * Go to previous slide.
   * @param {number} howManySlides - How many items to slide backward.
   * @param {function} callback - Optional callback function.
   */
  prev(howManySlides, callback) {
    // early return when there is nothing to slide
    if (this.innerElements.length <= this.perPage) {
      return;
    }

    if (!howManySlides) {
      howManySlides = this.config.slidePage ? this.perPage : 1;
    }

    const beforeChange = this.currentSlide;

    if (this.config.loop) {
      const isNewIndexClone = this.currentSlide - howManySlides < 0;
      if (isNewIndexClone) {
        this.disableTransition();

        const mirrorSlideIndex = this.currentSlide + this.innerElements.length;
        const mirrorSlideIndexOffset = this.perPage;
        const moveTo = mirrorSlideIndex + mirrorSlideIndexOffset;
        const offset = (this.config.rtl ? 1 : -1) * moveTo * (this.elementWidth / this.perPage);
        const dragDistance = this.config.draggable ? this.drag.endX - this.drag.startX : 0;

        this.sliderFrame.style.transform = `translate3d(${offset + dragDistance}px, 0, 0)`;
        this.currentSlide = mirrorSlideIndex - howManySlides;
      }
      else {
        this.currentSlide = this.currentSlide - howManySlides;
      }
    }
    else {
      this.currentSlide = Math.max(this.currentSlide - howManySlides, 0);
    }

    if (beforeChange !== this.currentSlide) {
      this.slideToCurrent(this.config.loop);
      this.config.onChange.call(this);
      this.change();
      if (callback) {
        callback.call(this);
      }
    }
  }

  /**
   * Go to next slide.
   * @param {number} howManySlides - How many items to slide forward.
   * @param {function} callback - Optional callback function.
   */
  next(howManySlides, callback) {
    // early return when there is nothing to slide
    if (this.innerElements.length <= this.perPage) {
      return;
    }

    if (!howManySlides) {
      howManySlides = this.config.slidePage ? this.perPage : 1;
    }

    const beforeChange = this.currentSlide;

    if (this.config.loop) {
      const isNewIndexClone = this.currentSlide + howManySlides > this.innerElements.length - this.perPage;
      if (isNewIndexClone) {
        this.disableTransition();

        const mirrorSlideIndex = this.currentSlide - this.innerElements.length;
        const mirrorSlideIndexOffset = this.perPage;
        const moveTo = mirrorSlideIndex + mirrorSlideIndexOffset;
        const offset = (this.config.rtl ? 1 : -1) * moveTo * (this.elementWidth / this.perPage);
        const dragDistance = this.config.draggable ? this.drag.endX - this.drag.startX : 0;

        this.sliderFrame.style.transform = `translate3d(${offset + dragDistance}px, 0, 0)`;
        this.currentSlide = mirrorSlideIndex + howManySlides;
      }
      else {
        this.currentSlide = this.currentSlide + howManySlides;
      }
    }
    else {
      this.currentSlide = Math.min(this.currentSlide + howManySlides, this.innerElements.length - this.perPage);
    }
    if (beforeChange !== this.currentSlide) {
      this.slideToCurrent(this.config.loop);
      this.config.onChange.call(this);
      this.change();
      if (callback) {
        callback.call(this);
      }
    }
  }

  /**
   * Disable transition on sliderFrame.
   */
  disableTransition() {
    this.sliderFrame.style.transition = `all 0ms ${this.config.easing}`;
  }

  /**
   * Enable transition on sliderFrame.
   */
  enableTransition() {
    this.sliderFrame.style.transition = `all ${this.config.duration}ms ${this.config.easing}`;
  }

  /**
   * Go to slide with particular index
   * @param {number} index - Item index to slide to.
   * @param {function} callback - Optional callback function.
   */
  goTo(index, callback) {
    if (this.innerElements.length <= this.perPage) {
      return;
    }
    const beforeChange = this.currentSlide;
    this.currentSlide = this.config.loop ?
      index % this.innerElements.length :
      Math.min(Math.max(index, 0), this.innerElements.length - this.perPage);
    if (beforeChange !== this.currentSlide) {
      this.slideToCurrent();
      this.config.onChange.call(this);
      this.change();
      if (callback) {
        callback.call(this);
      }
    }
  }

  /**
   * Moves sliders frame to position of currently active slide
   */
  slideToCurrent(enableTransition) {
    const currentSlide = this.config.loop ? this.currentSlide + this.perPage : this.currentSlide;
    const offset = (this.config.rtl ? 1 : -1) * currentSlide * (this.elementWidth / this.perPage);

    if (enableTransition) {
      // This one is tricky, I know but this is a perfect explanation:
      // https://youtu.be/cCOL7MC4Pl0
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.enableTransition();
          this.sliderFrame.style.transform = `translate3d(${offset}px, 0, 0)`;
        });
      });
    }
    else {
      this.sliderFrame.style.transform = `translate3d(${offset}px, 0, 0)`;
    }
  }

  /**
   * Recalculate drag /swipe event and reposition the frame of a slider
   */
  updateAfterDrag() {
    const movement = (this.config.rtl ? -1 : 1) * (this.drag.endX - this.drag.startX);
    const movementDistance = Math.abs(movement);
    let howManySliderToSlide = this.config.multipleDrag ? Math.ceil(movementDistance / (this.elementWidth / this.perPage)) : 1;

    if (this.config.slidePage) {
      howManySliderToSlide = Math.floor(howManySliderToSlide / 3) * 3;
    }

    const slideToNegativeClone = movement > 0 && this.currentSlide - howManySliderToSlide < 0;
    const slideToPositiveClone = movement < 0 && this.currentSlide + howManySliderToSlide > this.innerElements.length - this.perPage;

    if (movement > 0 && movementDistance > this.config.threshold && this.innerElements.length > this.perPage) {
      this.prev(howManySliderToSlide);
    }
    else if (movement < 0 && movementDistance > this.config.threshold && this.innerElements.length > this.perPage) {
      this.next(howManySliderToSlide);
    }
    this.slideToCurrent(slideToNegativeClone || slideToPositiveClone);

    this.autoplay = window.clearInterval(this.autoplay);
  }

  /**
   * When window resizes, resize slider components as well
   */
  resizeHandler() {
    // update perPage number dependable of user value
    this.resolveSlidesNumber();

    // relcalculate currentSlide
    // prevent hiding items when browser width increases
    if (this.currentSlide + this.perPage > this.innerElements.length) {
      this.currentSlide = this.innerElements.length <= this.perPage ? 0 : this.innerElements.length - this.perPage;
    }

    this.elementWidth = this.element.offsetWidth;

    this.buildSliderFrame();
  }

  /**
   * Clear drag after touchend and mouseup event
   */
  clearDrag() {
    this.drag = {
      startX: 0,
      endX: 0,
      startY: 0,
      letItGo: null,
      preventClick: this.drag.preventClick
    };
  }

  touchstartHandler(e) {
    // Prevent dragging / swiping on inputs, selects and textareas
    const ignoreSiema = ['TEXTAREA', 'OPTION', 'INPUT', 'SELECT'].indexOf(e.target.nodeName) !== -1;
    if (ignoreSiema) {
      return;
    }

    e.stopPropagation();
    this.pointerDown = true;
    this.drag.startX = e.touches[0].pageX;
    this.drag.startY = e.touches[0].pageY;
  }

  touchendHandler(e) {
    e.stopPropagation();
    this.pointerDown = false;
    this.enableTransition();
    if (this.drag.endX) {
      this.updateAfterDrag();
    }
    this.clearDrag();
  }

  touchmoveHandler(e) {
    e.stopPropagation();

    if (this.drag.letItGo === null) {
      this.drag.letItGo = Math.abs(this.drag.startY - e.touches[0].pageY) < Math.abs(this.drag.startX - e.touches[0].pageX);
    }

    if (this.pointerDown && this.drag.letItGo) {
      e.preventDefault();
      this.drag.endX = e.touches[0].pageX;
      this.sliderFrame.style.transition = `all 0ms ${this.config.easing}`;

      const currentSlide = this.config.loop ? this.currentSlide + this.perPage : this.currentSlide;
      const currentOffset = currentSlide * (this.elementWidth / this.perPage);
      const dragOffset = (this.drag.endX - this.drag.startX);
      const offset = this.config.rtl ? currentOffset + dragOffset : currentOffset - dragOffset;
      this.sliderFrame.style.transform = `translate3d(${(this.config.rtl ? 1 : -1) * offset}px, 0, 0)`;
    }
  }

  mousedownHandler(e) {
    // Prevent dragging / swiping on inputs, selects and textareas
    const ignoreSiema = ['TEXTAREA', 'OPTION', 'INPUT', 'SELECT'].indexOf(e.target.nodeName) !== -1;
    if (ignoreSiema) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    this.pointerDown = true;
    this.drag.startX = e.pageX;
  }

  mouseupHandler(e) {
    e.stopPropagation();
    this.pointerDown = false;
    this.element.style.cursor = 'grab';
    this.enableTransition();
    if (this.drag.endX) {
      this.updateAfterDrag();
    }
    this.clearDrag();
  }

  mousemoveHandler(e) {
    e.preventDefault();
    if (this.pointerDown) {
      // if dragged element is a link
      // mark preventClick prop as a true
      // to detemine about browser redirection later on
      if (e.target.nodeName === 'A') {
        this.drag.preventClick = true;
      }

      this.drag.endX = e.pageX;
      this.element.style.cursor = 'grabbing';
      this.sliderFrame.style.transition = `all 0ms ${this.config.easing}`;

      const currentSlide = this.config.loop ? this.currentSlide + this.perPage : this.currentSlide;
      const currentOffset = currentSlide * (this.elementWidth / this.perPage);
      const dragOffset = (this.drag.endX - this.drag.startX);
      const offset = this.config.rtl ? currentOffset + dragOffset : currentOffset - dragOffset;
      this.sliderFrame.style.transform = `translate3d(${(this.config.rtl ? 1 : -1) * offset}px, 0, 0)`;
    }
  }

  mouseleaveHandler(e) {
    if (this.pointerDown) {
      this.pointerDown = false;
      this.element.style.cursor = 'grab';
      this.drag.endX = e.pageX;
      this.drag.preventClick = false;
      this.enableTransition();
      this.updateAfterDrag();
      this.clearDrag();
    }
  }

  clickHandler(e) {
    // if the dragged element is a link
    // prevent browsers from folowing the link
    if (this.drag.preventClick) {
      e.preventDefault();
    }
    this.drag.preventClick = false;
  }

  /**
   * Remove item from carousel.
   * @param {number} index - Item index to remove.
   * @param {function} callback - Optional callback to call after remove.
   */
  remove(index, callback) {
    if (index < 0 || index >= this.innerElements.length) {
      throw new Error('Item to remove doesn\'t exist');
    }

    // Shift sliderFrame back by one item when:
    // 1. Item with lower index than currenSlide is removed.
    // 2. Last item is removed.
    const lowerIndex = index < this.currentSlide;
    const lastItem = this.currentSlide + this.perPage - 1 === index;

    if (lowerIndex || lastItem) {
      this.currentSlide--;
    }

    this.innerElements.splice(index, 1);

    // build a frame and slide to a currentSlide
    this.buildSliderFrame();

    if (callback) {
      callback.call(this);
    }
  }

  /**
   * Insert item to carousel at particular index.
   * @param {HTMLElement} item - Item to insert.
   * @param {number} index - Index of new new item insertion.
   * @param {function} callback - Optional callback to call after insert.
   */
  insert(item, index, callback) {
    if (index < 0 || index > this.innerElements.length + 1) {
      throw new Error('Unable to inset it at this index');
    }
    if (this.innerElements.indexOf(item) !== -1) {
      throw new Error('The same item in a carousel? Really? Nope');
    }

    // Avoid shifting content
    const shouldItShift = index <= this.currentSlide > 0 && this.innerElements.length;
    this.currentSlide = shouldItShift ? this.currentSlide + 1 : this.currentSlide;

    this.innerElements.splice(index, 0, item);

    // build a frame and slide to a currentSlide
    this.buildSliderFrame();

    if (callback) {
      callback.call(this);
    }
  }

  /**
   * Prepernd item to carousel.
   * @param {HTMLElement} item - Item to prepend.
   * @param {function} callback - Optional callback to call after prepend.
   */
  prepend(item, callback) {
    this.insert(item, 0);
    if (callback) {
      callback.call(this);
    }
  }

  /**
   * Append item to carousel.
   * @param {HTMLElement} item - Item to append.
   * @param {function} callback - Optional callback to call after append.
   */
  append(item, callback) {
    this.insert(item, this.innerElements.length + 1);
    if (callback) {
      callback.call(this);
    }
  }

  /**
   * Removes listeners and optionally restores to initial markup
   * @param {boolean} restoreMarkup - Determinants about restoring an initial markup.
   * @param {function} callback - Optional callback function.
   */
  destroy(restoreMarkup = false, callback) {
    this.detachEvents();

    this.element.style.cursor = 'auto';

    if (restoreMarkup) {
      const slides = document.createDocumentFragment();
      for (let i = 0; i < this.innerElements.length; i++) {
        slides.appendChild(this.innerElements[i]);
      }
      this.element.innerHTML = '';
      this.element.appendChild(slides);
      this.element.removeAttribute('style');
    }

    if (callback) {
      callback.call(this);
    }
  }

  /**
   * Creates the automated interval
   */
  initAutomation() {
    this.autoplay = window.setInterval(() => {
      if (this.currentSlide === this.innerElements.length - 1) return this.goTo(0);
      this.next();
    }, this.config.automatedTimer);

    this.element.addEventListener('touchstart', () => {
      this.autoplay = window.clearInterval(this.autoplay);
    });
  }

  /**
   * Sets the active class on the currently active dot
   */
  setActiveDot() {
    if (!this.dots) return;

    const activeDot = this.dots.find(el => el.classList.contains('is-active'));
    if (activeDot) activeDot.classList.remove('is-active');

    let activeDotIndex = this.currentSlide;

    if (this.config.slidePage && this.perPage > 1) {
      activeDotIndex = Math.ceil((this.currentSlide + 1) / this.perPage) - 1;
      if ((this.currentSlide + 1) % this.perPage !== 1) activeDotIndex += 1;
    }

    if (this.perPage > 1) {
      if (activeDotIndex < 0) activeDotIndex = 0;
      if (activeDotIndex > this.dots.length - 1) activeDotIndex = this.dots.length - 1;
    }

    this.dots[activeDotIndex].classList.add('is-active');
  }

  /**
   * Handles side-effects on change
   */
  change() {
    this.setActiveDot();

    // Disable buttons
    if (this.btnNext && !this.config.loop) {
      this.btnNext.disabled = false;
      if (this.currentSlide + this.perPage === this.innerElements.length) this.btnNext.disabled = true;
    }

    if (this.btnPrev && !this.config.loop) {
      this.btnPrev.disabled = false;
      if (this.currentSlide === 0) this.btnPrev.disabled = true;
    }

    // accessibility 
    this.innerElements.forEach((slide, i) => {
      if (i < this.currentSlide || i > this.currentSlide + this.perPage - 1) {
        this.hideA11y(slide.parentElement);
      } 
      else {
        this.showA11y(slide.parentElement);
      }
      if (this.config.loop) {
        this.loopSlidesPrepended.concat(this.loopSlidesAppended).forEach(loopSlide => this.hideA11y(loopSlide));
        if (this.currentSlide < 0) {          
          for (let i = this.loopSlidesPrepended.length + this.currentSlide; i < this.loopSlidesPrepended.length; i++) {
            this.showA11y(this.loopSlidesPrepended[i]);
          }
        }
        else if (this.currentSlide > this.innerElements.length - this.perPage) {
          for (let i = 0; i <= this.perPage - (this.innerElements.length - this.currentSlide) - 1; i++) {
            this.showA11y(this.loopSlidesAppended[i]);
          }
        }
      }
    });
  }
}

// Attach to jquery if present
if (window.jQuery) {
  $.fn.siema = function(options) {
    return this.each(function() {
      if (!$.data(this, 'siema')) {
        $.data(this, 'siema', new Siema({
          ...options,
          selector: this
        }));
      }
    });
  };
}
